import { getAccessToken } from '../supabaseClient';
import { AuthTokenExpiredError, AccessDeniedError } from './sites';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:4000';

const buildUrl = (path: string) => {
  const base = API_BASE_URL.replace(/\/*$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
};

const extractErrorMessage = (details: unknown, fallback: string): string => {
  if (!details) {
    return fallback;
  }

  if (typeof details === 'string' && details.trim().length > 0) {
    return details;
  }

  if (typeof details === 'object') {
    const record = details as Record<string, unknown>;
    for (const key of ['message', 'error', 'details'] as const) {
      const value = record[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }
  }

  return fallback;
};

export interface ClientUserSummary {
  id: string;
  email: string;
  fullName: string | null;
  createdAt: string | null;
  createdByUserId: string | null;
  role: string | null;
}

type UserRole = 'admin' | 'admin_client' | 'client';

interface FetchManagedClientsOptions {
  roles?: UserRole[];
  createdBy?: 'me' | string | null;
}

interface UserListResponse {
  users: ClientUserSummary[];
}

interface InviteClientPayload {
  email: string;
  fullName?: string;
  role?: string;
}

interface InviteClientResponse {
  user: ClientUserSummary;
}

interface UpdateUserPayload {
  fullName?: string;
  role?: string;
}

interface UpdateUserResponse {
  user: ClientUserSummary;
}

export const fetchManagedClients = async (options?: FetchManagedClientsOptions): Promise<ClientUserSummary[]> => {
  const token = await getAccessToken();
  const params = new URLSearchParams();
  const roles = options?.roles ?? ['client'];

  if (roles.length > 0) {
    params.set('roles', roles.join(','));
  }

  const createdByOption = options?.createdBy;
  if (createdByOption === undefined) {
    params.set('createdBy', 'me');
  } else if (createdByOption === null) {
    // Don't set createdBy parameter to get all users
  } else if (createdByOption !== null) {
    const trimmed = createdByOption.trim();
    if (trimmed.length > 0) {
      params.set('createdBy', trimmed);
    }
  }

  const query = params.toString();
  const response = await fetch(buildUrl(`/api/users${query ? `?${query}` : ''}`), {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthTokenExpiredError();
    }
    if (response.status === 403) {
      throw new AccessDeniedError();
    }

    let details: unknown = null;
    try {
      details = await response.json();
    } catch (error) {
      // ignore parse errors
    }

    throw new Error(extractErrorMessage(details, 'Failed to load client users.'));
  }

  const { users } = (await response.json()) as UserListResponse;
  return users;
};

export const inviteClientAccount = async ({ email, fullName, role }: InviteClientPayload): Promise<ClientUserSummary> => {
  const token = await getAccessToken();
  const response = await fetch(buildUrl('/api/users/clients/invite'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, fullName, role })
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthTokenExpiredError();
    }
    if (response.status === 403) {
      throw new AccessDeniedError();
    }

    let details: unknown = null;
    try {
      details = await response.json();
    } catch (error) {
      // ignore parse errors
    }

    throw new Error(extractErrorMessage(details, 'Failed to invite client account.'));
  }

  const { user } = (await response.json()) as InviteClientResponse;
  return user;
};

export const updateClientUser = async (userId: string, { fullName, role }: UpdateUserPayload): Promise<ClientUserSummary> => {
  const token = await getAccessToken();
  const response = await fetch(buildUrl(`/api/users/clients/${encodeURIComponent(userId)}`), {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ fullName, role })
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthTokenExpiredError();
    }
    if (response.status === 403) {
      throw new AccessDeniedError();
    }

    let details: unknown = null;
    try {
      details = await response.json();
    } catch (error) {
      // ignore parse errors
    }

    throw new Error(extractErrorMessage(details, 'Failed to update user.'));
  }

  const { user } = (await response.json()) as UpdateUserResponse;
  return user;
};

export const deleteClientAccount = async (clientUserId: string): Promise<void> => {
  const token = await getAccessToken();
  const response = await fetch(buildUrl(`/api/users/clients/${encodeURIComponent(clientUserId)}`), {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthTokenExpiredError();
    }
    if (response.status === 403) {
      throw new AccessDeniedError();
    }

    let details: unknown = null;
    try {
      details = await response.json();
    } catch (error) {
      // ignore parse errors
    }

    throw new Error(extractErrorMessage(details, 'Failed to delete client account.'));
  }

  // Success - no content returned for DELETE
};
